import { Select } from 'antd5';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ClearIcon, SuffixIcon } from 'components/SelectChooser/SelectChooser.style';
import { useBladeDetailsQuery as useAtlasBladeQuery } from 'types/atlas-graphql';
import { notEmpty } from 'utils/types';

import { useAssetChooserState } from '../AssetChooser/useAssetChooserState';
import { filterOption } from '../helpers';

export type BladeChooserProps = {
  turbineId: string;
  value?: string | string[];
  onChange: (value: string | string[]) => void;
  disabled?: boolean;
  mode?: 'multiple';
};

const useBladeChooserData = ({ turbineId }: { turbineId: string }) => {
  const { data: atlasData, loading: atlasLoading } = useAtlasBladeQuery({
    variables: { turbineId },
    skip: !turbineId,
  });

  return useMemo(() => {
    if (atlasLoading) {
      return { data: undefined, loading: true };
    }

    if (atlasData?.assetWithMetadata.__typename !== 'Turbine') {
      return { data: undefined, loading: false };
    }

    const blades =
      atlasData?.assetWithMetadata?.descendants?.flatMap((x: any) => {
        if (x.__typename !== 'Blade') {
          return [];
        }
        return {
          id: x.id,
          label: x.uniqueIdLabel,
          serial: x.serial,
        };
      }) ?? [];

    return {
      data: {
        turbine: {
          id: turbineId,
          blades,
        },
      },
      loading: atlasLoading,
    };
  }, [atlasData, atlasLoading]);
};

export default function BladeChooser({
  turbineId,
  value,
  onChange,
  disabled,
  mode = 'multiple',
}: BladeChooserProps) {
  const { t } = useTranslation();
  const { data, loading } = useBladeChooserData({ turbineId });
  const blades = data?.turbine?.blades ?? [];

  const { handleChange, setVisible, chooserState } = useAssetChooserState({ onChange, value });

  return (
    <Select
      id="single-turbine-blade-chooser"
      placeholder={t('components.selectBlades', 'Select Blades...')}
      loading={loading}
      allowClear
      showSearch
      filterOption={filterOption}
      mode={mode}
      maxTagCount={1}
      value={loading ? undefined : chooserState}
      onChange={handleChange}
      onDropdownVisibleChange={setVisible}
      disabled={disabled}
      showArrow={!chooserState}
      clearIcon={<ClearIcon />}
      suffixIcon={<SuffixIcon />}
      virtual={false}
    >
      {/* PAPI blades can be null */}
      {blades.filter(notEmpty).map(blade => (
        <Select.Option
          key={blade.id}
          className="single-turbine-blade-chooser-option"
          value={blade.id ?? ''}
        >
          {blade.serial ? `${blade.label} (#${blade.serial})` : blade.label}
        </Select.Option>
      ))}
    </Select>
  );
}
